// src/pages/Processing.js

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useLanguage } from "../context/LanguageContext";
import translations from "../data/translations";
import AIZzra from "../assets/AI.png";

const StepBar = ({ current, total }) => (
  <div className="d-flex align-items-center gap-2 mb-4">
    {Array.from({ length: total }, (_, i) => {
      const done = i + 1 < current;
      const active = i + 1 === current;
      return (
        <React.Fragment key={i}>
          <div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
            style={{
              width: 28, height: 28,
              background: done || active ? "#0099CC" : "#e6eef3",
              color: done || active ? "#fff" : "#aaa",
              fontSize: "0.72rem", fontWeight: 700, transition: "background 0.3s"
            }}>
            {done ? "✓" : i + 1}
          </div>
          {i < total - 1 && (
            <div style={{ flex: 1, height: 3, borderRadius: 2, background: done ? "#0099CC" : "#e6eef3", transition: "background 0.3s" }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

const Processing = () => {
  const navigate = useNavigate();
  const { service, reason, phone, network } = useLocation().state || {};
  const [step, setStep] = useState(1);
  const [progress, setProgress] = useState(0);

  const { language } = useLanguage();
  const t = translations[language] || translations["English"];

  const svc = service || {
    name: "Wallet Protection Plan", type: "Insurance",
    pricePerMonth: 99, totalDeducted: 297,
    maxCoverage: "Rs. 15,000", icon: "🛡️"
  };

  // Auto-advance step 2 → 3
  useEffect(() => {
    if (step === 2) {
      const interval = setInterval(() => setProgress(p => Math.min(p + 2, 100)), 60);
      const timer = setTimeout(() => { clearInterval(interval); setStep(3); }, 4000);
      return () => { clearInterval(interval); clearTimeout(timer); };
    }
  }, [step]);

  const stepTitles = [
    t.processingConfirmTitle,
    t.processingTitle,
    t.cancelDoneTitle,
    t.refundTitle,
    t.thankYouTitle,
  ];

  const deducted = svc.totalDeducted || 297;
  const complimentary = deducted;
  const total = deducted + complimentary;

  return (
    <div className="container-fluid py-4 px-3 px-md-4" style={{ maxWidth: "900px", margin: "0 auto" }}>
      <Header showBack onBack={step > 1 && step < 5 ? () => setStep(s => s - 1) : () => navigate(-1)} />

      <div className="card shadow-sm border-0 rounded-4 p-4 mb-4">
        <StepBar current={step} total={5} />

        <p style={{ fontSize: "0.72rem", color: "#0099CC", fontWeight: 700, letterSpacing: "0.5px", marginBottom: "16px", textTransform: "uppercase" }}>
          Step {step} / 5 — {stepTitles[step - 1]}
        </p>

        {/* ── Step 1: Confirm ── */}
        {step === 1 && (
          <div>
            <p style={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a2e", marginBottom: "4px" }}>
              {t.processingConfirmTitle}
            </p>
            <p style={{ fontSize: "0.8rem", color: "#888", marginBottom: "20px" }}>
              {t.processingConfirmSub}
            </p>
            <div className="rounded-3 p-3 mb-4 d-flex align-items-center gap-3"
              style={{ border: "1.5px solid #e6eef3", background: "#fafcfe" }}>
              <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 48, height: 48, background: "#e8f6fb", fontSize: "1.4rem" }}>
                {svc.icon}
              </div>
              <div>
                <p style={{ fontWeight: 700, fontSize: "0.88rem", color: "#1a1a2e", marginBottom: "2px" }}>{svc.name}</p>
                <p style={{ fontSize: "0.78rem", color: "#888", marginBottom: 0 }}>
                  Rs. {svc.pricePerMonth} / {t.month || "month"}
                </p>
              </div>
            </div>
            <button onClick={() => setStep(2)}
              style={{ width: "100%", padding: "13px", background: "#0099CC", color: "white", border: "none", borderRadius: "8px", fontSize: "1rem", fontWeight: 600, cursor: "pointer", marginBottom: "10px", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#007aaa"}
              onMouseLeave={e => e.currentTarget.style.background = "#0099CC"}>
              {t.processingConfirmBtn}
            </button>
            <button onClick={() => navigate(-1)}
              style={{ width: "100%", padding: "13px", background: "white", color: "#0099CC", border: "1.5px solid #0099CC", borderRadius: "8px", fontSize: "1rem", fontWeight: 600, cursor: "pointer", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#e6f5fb"}
              onMouseLeave={e => e.currentTarget.style.background = "white"}>
              {t.processingBackBtn}
            </button>
          </div>
        )}

        {/* ── Step 2: Processing ── */}
        {step === 2 && (
          <div className="text-center py-4">
            <div style={{ fontSize: "3.5rem", marginBottom: "16px" }}>📄</div>
            <p style={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a2e", marginBottom: "8px" }}>
              {t.processingTitle}
            </p>
            <p style={{ fontSize: "0.82rem", color: "#888", marginBottom: "24px" }}>
              {t.processingSub}
            </p>
            <div style={{ background: "#e6eef3", borderRadius: "20px", height: "10px", overflow: "hidden" }}>
              <div style={{
                width: `${progress}%`, height: "100%",
                background: "linear-gradient(90deg, #0099CC, #00c8f0)",
                borderRadius: "20px", transition: "width 0.1s linear"
              }} />
            </div>
          </div>
        )}

        {/* ── Step 3: Done ── */}
        {step === 3 && (
          <div className="text-center py-4">
            <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#edfaf3", margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2rem" }}>✅</div>
            <p style={{ fontWeight: 700, fontSize: "1.05rem", color: "#1a1a2e", marginBottom: "8px" }}>{t.cancelDoneTitle}</p>
            <p style={{ fontSize: "0.82rem", color: "#666", marginBottom: "6px" }}>{t.cancelDoneSub}</p>
            <p style={{ fontSize: "0.82rem", color: "#666", marginBottom: "28px" }}>{t.cancelDoneSub2}</p>
            <button onClick={() => setStep(4)}
              style={{ padding: "12px 32px", background: "#0099CC", color: "white", border: "none", borderRadius: "8px", fontSize: "0.95rem", fontWeight: 600, cursor: "pointer", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#007aaa"}
              onMouseLeave={e => e.currentTarget.style.background = "#0099CC"}>
              {t.cancelDoneNext}
            </button>
          </div>
        )}

        {/* ── Step 4: Refund ── */}
        {step === 4 && (
          <div>
            <div className="d-flex align-items-center gap-2 mb-3">
              <img
                src={AIZzra}
                alt="LARKI"
                style={{ width: 40, height: 40, objectFit: "contain" }}
              />
              <p style={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a2e", marginBottom: 0 }}>{t.refundTitle}</p>
            </div>
            {/* <p style={{ fontSize: "0.82rem", color: "#555", marginBottom: "20px", lineHeight: 1.6 }}>
              {t.refundDesc}
            </p>
            <div className="rounded-3 mb-4 overflow-hidden" style={{ border: "1.5px solid #e6eef3" }}>
              <div className="p-3" style={{ background: "#fafcfe" }}>
                <p style={{ fontWeight: 700, fontSize: "0.85rem", color: "#1a1a2e", marginBottom: "12px" }}>
                  {t.refundSummaryTitle}
                </p>
                {[
                  { label: t.refundDeducted, val: `Rs. ${deducted}` },
                  // { label: t.refundComplimentary, val: `Rs. ${complimentary}` },
                ].map((row, i) => (
                  <div key={i} className="d-flex justify-content-between mb-2">
                    <span style={{ fontSize: "0.8rem", color: "#666" }}>{row.label}</span>
                    <span style={{ fontSize: "0.8rem", color: "#1a1a2e", fontWeight: 600 }}>{row.val}</span>
                  </div>
                ))}
              </div>
              <div className="d-flex justify-content-between p-3" style={{ background: "#e6f5fb", borderTop: "1.5px solid #b8dff0" }}>
                <span style={{ fontWeight: 700, fontSize: "0.85rem", color: "#1a1a2e" }}>{t.refundTotal}</span>
                <span style={{ fontWeight: 700, fontSize: "0.88rem", color: "#0099CC" }}>Rs. {deducted}</span>
              </div>
            </div> */}
            <p style={{ fontSize: "0.78rem", color: "#888", marginBottom: "24px" }}>{t.refundNote}</p>
            <button onClick={() => setStep(5)}
              style={{ width: "100%", padding: "13px", background: "#0099CC", color: "white", border: "none", borderRadius: "8px", fontSize: "1rem", fontWeight: 600, cursor: "pointer", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#007aaa"}
              onMouseLeave={e => e.currentTarget.style.background = "#0099CC"}>
              {t.refundNextBtn}
            </button>
          </div>
        )}

        {/* ── Step 5: Thank you ── */}
        {step === 5 && (
          <div className="text-center py-4">
            <div style={{ width: 90, height: 90, borderRadius: "50%", background: "#e6f5fb", margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 24px rgba(0, 153, 204, 0.12)" }}>
              <img
                src={AIZzra}
                alt="LARKI"
                style={{ width: 120, height: 120, objectFit: "contain" }}
              />
            </div>
            <p style={{ fontWeight: 700, fontSize: "1.1rem", color: "#1a1a2e", marginBottom: "8px" }}>{t.thankYouTitle}</p>
            <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: "6px" }}>{t.thankYouSub}</p>
            <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: "32px" }}>{t.thankYouSub2}</p>
            <button onClick={() => navigate("/")}
              style={{ padding: "13px 36px", background: "#0099CC", color: "white", border: "none", borderRadius: "8px", fontSize: "1rem", fontWeight: 600, cursor: "pointer", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#007aaa"}
              onMouseLeave={e => e.currentTarget.style.background = "#0099CC"}>
              {t.goHomeBtn}
            </button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Processing;